import { useCallback, useRef, useState } from "react";

export const LIVE_SAVED_SEARCH_PAGE_SIZE = 25;

/**
 * Cursor paging for live Saved Search results, mirroring Search's pager guarantee:
 * a page is offered only once its start cursor is known (`next_cursor` of the page
 * before it), so every page change costs exactly one search request.
 */
export function useLiveSearchPaging() {
  const pageStartCursorsRef = useRef([null]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(LIVE_SAVED_SEARCH_PAGE_SIZE);
  const [lastReachablePage, setLastReachablePage] = useState(0);

  /** Request pagination for `targetPage`; page 0 restarts the cursor chain. */
  const paginationFor = useCallback((targetPage) => {
    if (targetPage === 0) pageStartCursorsRef.current = [null];
    const cursor = pageStartCursorsRef.current[targetPage];
    return { page_size: rowsPerPage, ...(cursor ? { cursor } : {}) };
  }, [rowsPerPage]);

  /** Record a landed page and the start cursor of the page after it. */
  const recordPage = useCallback((targetPage, nextCursor) => {
    const cursors = pageStartCursorsRef.current.slice(0, targetPage + 1);
    if (nextCursor) cursors[targetPage + 1] = nextCursor;
    pageStartCursorsRef.current = cursors;
    setPage(targetPage);
    setLastReachablePage(cursors.length - 1);
  }, []);

  const canLoadPage = useCallback(
    (targetPage) => targetPage === 0 || Boolean(pageStartCursorsRef.current[targetPage]),
    []
  );

  return { page, rowsPerPage, setRowsPerPage, lastReachablePage, paginationFor, recordPage, canLoadPage };
}
